/**
 * Three headline cards row: today's audits, cost-per-audit (7d), and
 * daily run-rate projected to month-end. Each card carries a sparkline
 * built from the same 30-day series so the visual scale matches.
 */
import { TrendingDown, TrendingUp } from "lucide-react";
import { Sparkline } from "./Sparkline";
import { ViewQueryButton } from "./ViewQueryButton";
import { formatInt, formatUsd, formatUsd2 } from "./format";
import type { ApiEnvelope, HeadlineData } from "./types";

interface HeadlineCardsProps {
  envelope: ApiEnvelope<HeadlineData> | undefined;
  loading: boolean;
}

function Card({
  title,
  primary,
  secondary,
  sparkline,
  footnote,
}: {
  title: string;
  primary: string;
  secondary?: string;
  sparkline?: number[];
  footnote?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-xs uppercase tracking-wide text-zinc-500">{title}</h3>
          <div className="mt-1 text-2xl font-semibold text-zinc-900 dark:text-zinc-50">
            {primary}
          </div>
          {secondary && (
            <div className="mt-0.5 text-xs text-zinc-500">{secondary}</div>
          )}
        </div>
        {sparkline && sparkline.length > 0 && (
          <div className="text-blue-500 dark:text-blue-400 w-24 h-8">
            <Sparkline values={sparkline} width={96} height={32} />
          </div>
        )}
      </div>
      {footnote && (
        <div className="mt-3 pt-3 border-t border-zinc-200 dark:border-zinc-700 text-xs text-zinc-500">
          {footnote}
        </div>
      )}
    </div>
  );
}

export function HeadlineCards({ envelope, loading }: HeadlineCardsProps) {
  const data = envelope?.data;
  const sparkValues =
    data?.sparkline_30d.map((d) => d.total_cost) ?? [];

  const todayCount = data?.today.audit_count ?? 0;
  const isEmpty = !loading && todayCount === 0;

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
          Today &amp; trailing
        </h2>
        <ViewQueryButton query={envelope?.query} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card
          title="Audits today"
          primary={loading ? "…" : formatInt(todayCount)}
          secondary={
            isEmpty
              ? "No audits yet today"
              : `avg ${formatUsd(data?.today.avg_cost_per_audit ?? 0)} • infra ${formatUsd(
                  data?.today.infra_cost ?? 0
                )} + vendor ${formatUsd(data?.today.vendor_cost ?? 0)}`
          }
          sparkline={
            sparkValues.length > 0
              ? data?.sparkline_30d.map((d) => d.audit_count) ?? []
              : undefined
          }
        />
        <Card
          title="Cost per audit (7d)"
          primary={loading ? "…" : formatUsd(data?.cost_per_audit_7d.avg ?? 0)}
          secondary={
            data
              ? `p50 ${formatUsd(data.cost_per_audit_7d.p50)} • p95 ${formatUsd(
                  data.cost_per_audit_7d.p95
                )} • p99 ${formatUsd(data.cost_per_audit_7d.p99)}`
              : undefined
          }
          sparkline={sparkValues}
        />
        <Card
          title="Daily run-rate $"
          primary={loading ? "…" : formatUsd2(data?.run_rate.projected_month_end ?? 0)}
          secondary={
            data
              ? `projected month-end (day ${data.run_rate.day_of_month}/${data.run_rate.days_in_month})`
              : undefined
          }
          sparkline={sparkValues}
          footnote={
            data ? (
              <span className="inline-flex items-center gap-1">
                {data.run_rate.delta_vs_last_month >= 0 ? (
                  <TrendingUp className="h-3 w-3 text-red-500" />
                ) : (
                  <TrendingDown className="h-3 w-3 text-green-500" />
                )}
                {formatUsd2(Math.abs(data.run_rate.delta_vs_last_month))} vs last month (
                {formatUsd2(data.run_rate.last_month_total)})
              </span>
            ) : null
          }
        />
      </div>
    </section>
  );
}
